import type { ComponentType } from 'react';
import { Barracks, BarracksTile } from './buildings/barracks';
import { City } from './buildings/city';
import { Factory, FactoryTile } from './buildings/factory';
import { Farm, FarmTile } from './buildings/farm';
import { Forestry, ForestryTile } from './buildings/forestry';
import { Mine, MineTile } from './buildings/mine';
import { ShieldGenerator } from './buildings/shield';
import { Silo } from './buildings/silo';
import { Watchtower } from './buildings/watchtower';
import { Figure, type ModelProps } from './parts';
import { Archer, Cavalry, Ram, Scout, Settler, Warrior, Worker } from './units/classical';
import { Spy } from './units/covert';
import { DroneSwarm, Mech } from './units/future';
import { Artillery, Labourer, RifleSquad } from './units/industrial';
import { GunSquad, MissileLauncher, Tank } from './units/modern';
import { Engineer, Healer, Medic } from './units/support';

export type ModelComponent = ComponentType<ModelProps>;

/** Unit type → model. A new unit's look is one entry here. */
export const UNIT_MODELS: Record<string, ModelComponent> = {
  worker: Worker,
  labourer: Labourer,
  scout: Scout,
  settler: Settler,
  warrior: Warrior,
  archer: Archer,
  cavalry: Cavalry,
  ram: Ram,
  'rifle-squad': RifleSquad,
  artillery: Artillery,
  'gun-squad': GunSquad,
  tank: Tank,
  'missile-launcher': MissileLauncher,
  mech: Mech,
  'drone-swarm': DroneSwarm,
  healer: Healer,
  medic: Medic,
  engineer: Engineer,
  spy: Spy,
};

/** Building type → model (the city picks its look by level from `CITY_STAGES`). */
export const BUILDING_MODELS: Record<string, ModelComponent> = {
  city: City,
  farm: Farm,
  forestry: Forestry,
  barracks: Barracks,
  mine: Mine,
  factory: Factory,
  'missile-silo': Silo,
  'shield-generator': ShieldGenerator,
  watchtower: Watchtower,
};

/** Building type → what covers each extra tile of its grown footprint (drawn at the building's level). */
const FOOTPRINT_MODELS: Record<string, ModelComponent> = {
  farm: FarmTile,
  forestry: ForestryTile,
  barracks: BarracksTile,
  mine: MineTile,
  factory: FactoryTile,
};

/** Unit types with no model yet are a plain figure. */
export function unitModelFor(type: string): ModelComponent {
  return UNIT_MODELS[type] ?? Figure;
}

/** Unknown buildings (and footprint tiles) fall back to the farm's look. */
export function buildingModelFor(type: string): ModelComponent {
  return BUILDING_MODELS[type] ?? Farm;
}

export function footprintModelFor(type: string): ModelComponent {
  return FOOTPRINT_MODELS[type] ?? FarmTile;
}
